import { useState, useRef } from "react";
import { Phone, Mail, Instagram, MapPin, Check, Globe } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Divider, Overline } from "@/components/layout/Atoms";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/contexts/ContentContext";

const EVENT_TYPES = ["Mariage", "Anniversaire", "Baptême", "Séminaire d'entreprise", "Cocktail / Lancement", "Dîner privé", "Autre"];

const initial = { name: "", email: "", phone: "", event_type: "Mariage", event_date: "", guests: "", message: "", website: "" };

export default function Contact() {
    const c = useContent();
    const cp = c.contact_page;
    const ct = c.contact;
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const openedAtRef = useRef(Date.now());

    function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function onSubmit(e) {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.guests) {
            toast.error("Merci de renseigner les champs obligatoires.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/devis", {
                ...form,
                guests: Number(form.guests),
                elapsed_ms: Date.now() - openedAtRef.current,
            });
            setDone(true);
            toast.success("Votre demande de devis a bien été envoyée !");
            setForm(initial);
            openedAtRef.current = Date.now();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="page-contact">
            <section className="container-k pt-10 pb-12">
                <div className="max-w-3xl">
                    <Overline>{cp.hero.overline}</Overline>
                    <h1 className="mt-4 font-heading font-light text-5xl md:text-7xl leading-[1.0] text-kayama-ink">
                        {cp.hero.h1_part1} <span className="italic text-kayama-pink">{cp.hero.h1_italic}</span>{cp.hero.h1_part2}
                    </h1>
                    <p className="mt-6 text-kayama-brown-2 text-lg max-w-2xl">{cp.hero.subtitle}</p>
                </div>
                <Divider />
            </section>

            <section className="container-k pb-24">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    <Reveal className="lg:col-span-5">
                        <div className="bg-kayama-brown text-kayama-cream p-10 md:p-12 relative overflow-hidden">
                            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-kayama-pink/30 blur-3xl" />
                            <div className="relative">
                                <Overline className="text-kayama-gold">{cp.coords_overline}</Overline>
                                <h2 className="mt-3 font-heading text-4xl font-light">{cp.coords_title}</h2>
                                <p className="script text-4xl text-kayama-gold-light mt-3">{cp.coords_script}</p>

                                <ul className="mt-10 space-y-6">
                                    <li className="flex items-start gap-4" data-testid="contact-phone">
                                        <div className="w-10 h-10 bg-kayama-pink flex items-center justify-center shrink-0"><Phone size={18} /></div>
                                        <div>
                                            <div className="overline text-kayama-gold">Téléphone</div>
                                            <a href={`tel:${ct.phone_href}`} className="block mt-1 text-lg font-heading">{ct.phone}</a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4" data-testid="contact-email">
                                        <div className="w-10 h-10 bg-kayama-pink flex items-center justify-center shrink-0"><Mail size={18} /></div>
                                        <div>
                                            <div className="overline text-kayama-gold">Email</div>
                                            <a href={`mailto:${ct.email}`} className="block mt-1 text-lg font-heading break-all">{ct.email}</a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4" data-testid="contact-instagram">
                                        <div className="w-10 h-10 bg-kayama-pink flex items-center justify-center shrink-0"><Instagram size={18} /></div>
                                        <div>
                                            <div className="overline text-kayama-gold">Instagram</div>
                                            <a href={ct.instagram_url} target="_blank" rel="noreferrer" className="block mt-1 text-lg font-heading">{ct.instagram_handle}</a>
                                        </div>
                                    </li>
                                    {ct.website_label && (
                                        <li className="flex items-start gap-4" data-testid="contact-website">
                                            <div className="w-10 h-10 bg-kayama-pink flex items-center justify-center shrink-0"><Globe size={18} /></div>
                                            <div>
                                                <div className="overline text-kayama-gold">Site web</div>
                                                <a href={ct.website_url || `https://${ct.website_label}`} target="_blank" rel="noreferrer" className="block mt-1 text-lg font-heading break-all">{ct.website_label}</a>
                                            </div>
                                        </li>
                                    )}
                                    <li className="flex items-start gap-4" data-testid="contact-zone">
                                        <div className="w-10 h-10 bg-kayama-pink flex items-center justify-center shrink-0"><MapPin size={18} /></div>
                                        <div>
                                            <div className="overline text-kayama-gold">Zones d'intervention</div>
                                            <p className="mt-1 text-lg font-heading">{ct.zone}</p>
                                        </div>
                                    </li>
                                </ul>

                                <div className="mt-10 pt-8 border-t border-kayama-cream/15">
                                    <p className="script text-4xl text-kayama-gold-light">{c.brand.script_gold}</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={200} className="lg:col-span-7">
                        {done ? (
                            <div className="bg-kayama-paper border border-kayama-gold p-12 text-center" data-testid="devis-success">
                                <div className="w-16 h-16 rounded-full bg-kayama-pink text-white flex items-center justify-center mx-auto"><Check size={28} /></div>
                                <h3 className="mt-6 font-heading text-3xl text-kayama-brown">{cp.success_title}</h3>
                                <p className="script text-4xl text-kayama-gold mt-2">{cp.success_script}</p>
                                <p className="mt-4 text-kayama-brown-2 max-w-md mx-auto">{cp.success_text}</p>
                                <button onClick={() => setDone(false)} className="btn-ghost-k mt-8" data-testid="devis-new-request">Nouvelle demande</button>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="bg-kayama-paper border border-kayama-line p-8 md:p-12" data-testid="devis-form">
                                {/* Honeypot anti-spam */}
                                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                                    <label>Site web</label>
                                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => update("website", e.target.value)} />
                                </div>
                                <Overline>{cp.form_overline}</Overline>
                                <h2 className="mt-3 font-heading text-3xl md:text-4xl font-light text-kayama-brown">{cp.form_title}</h2>

                                <div className="grid md:grid-cols-2 gap-x-8 gap-y-7 mt-10">
                                    <div>
                                        <label className="label-k">Nom complet *</label>
                                        <input className="input-k" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="devis-name" />
                                    </div>
                                    <div>
                                        <label className="label-k">Email *</label>
                                        <input type="email" className="input-k" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="devis-email" />
                                    </div>
                                    <div>
                                        <label className="label-k">Téléphone *</label>
                                        <input className="input-k" value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="devis-phone" />
                                    </div>
                                    <div>
                                        <label className="label-k">Type d'événement *</label>
                                        <select className="input-k" value={form.event_type} onChange={(e) => update("event_type", e.target.value)} data-testid="devis-event-type">
                                            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="label-k">Date souhaitée</label>
                                        <input type="date" className="input-k" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} data-testid="devis-date" />
                                    </div>
                                    <div>
                                        <label className="label-k">Nombre de convives *</label>
                                        <input type="number" min="1" className="input-k" value={form.guests} onChange={(e) => update("guests", e.target.value)} required data-testid="devis-guests" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="label-k">Votre message</label>
                                        <textarea rows={4} className="input-k resize-none" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Parlez-nous de votre projet, vos envies, votre ambiance souhaitée…" data-testid="devis-message" />
                                    </div>
                                </div>

                                <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                    <p className="text-xs text-kayama-brown-3">* champs obligatoires · réponse sous 24 h</p>
                                    <button type="submit" disabled={submitting} className="btn-primary-k disabled:opacity-60" data-testid="devis-submit">
                                        {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
